import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { UrlService, UtilityService } from '@eforall/common';
import { AcceleratorFuncData, AccTeamFuncData, AccVentureFuncData, SessionAttendeeStatus } from '@interfaces';
import { AppService, FuncService } from '../../../services';
import { calculateGraduationScore } from './calculate-graduation-score';


export type AccNameStatus
	= { status: 'None', accName: '' }
	| { status: 'Loading', accName: string }
	| { status: 'Invalid', accName: string }
	| { status: 'Loaded', accName: string };


@Injectable({ providedIn: 'root' })
export class AcceleratorAreaService {

	private func = inject(FuncService);
	private urlService = inject(UrlService);
	private util = inject(UtilityService);
	private appService = inject(AppService);

	public readonly accNameStatus = signal<AccNameStatus>({ status: 'None', accName: '' }, { equal: (v1, v2) => { return v1.status == v2.status && v1.accName == v2.accName } });
	public readonly data = signal<AcceleratorFuncData | undefined>(undefined);

	public readonly canEdit = computed(() => this.appService.data().permissions.canFacilitateCohorts);

	constructor() {
		effect(() => {

			//
			// Changes to either of these signals will rerun this effect
			//
			const url = this.urlService.url();
			const accNameStatus = this.accNameStatus();

			let accName: string | undefined = undefined;
			const parts = url.split('/delivery/accelerators');
			const accPath = parts[1];

			if (accPath) {
				const slugs = accPath.split('/');
				accName = slugs[1];
				if (accName) {

					if (accNameStatus.status !== 'Loaded' || accNameStatus.accName !== accName) {

						if (accNameStatus.status !== 'Loading' && accNameStatus.accName !== accName) {
							this.accNameStatus.set({ status: 'Loading', accName });
						}
						this.reloadData();
					}
				}
			}

			if (accName == undefined) {
				if (accNameStatus.status !== 'None') this.accNameStatus.set({ status: 'None', accName: '' });
			}
		});
	}


	public async reloadData() {

		const { status, accName } = this.accNameStatus();
		if (status == 'None') return;

		const data = await this.func.accelerator.getData({ accName });

		if (data) {
			if (status !== 'Loaded') this.accNameStatus.set({ status: 'Loaded', accName });
			calculateGraduationScore(this.util, data);
			this.data.set(data);
		}
		else {
			if (status !== 'Invalid') this.accNameStatus.set({ status: 'Invalid', accName });
			this.data.set(undefined);
		}
	}


	async addEngagementIdToContactRequest(contactRequestId: number, engagementId: number) {
		await this.func.contactRequest.addEngagementId({ contactRequestId, engagementId });
	}

	public accTeamMap = computed(() => {
		const data = this.data();
		if (!data) return {};

		const ventureMap = data.ventures.reduce((map, v) => {
			const key = v.accTeamId;
			if (!map[key]) {
				map[key] = [];
			}
			map[key].push(v.ventureName);
			return map;
		}, {} as Record<number, string[]>);

		return data.teams.reduce((map, t) => {
			map[t.accTeamId] = {
				...t,
				ventures: ventureMap[t.accTeamId]?.join(', '),
			};
			return map;
		}, {} as Record<number, AccTeamFuncData & { ventures: string | undefined }>);
	});

	public accVentureMap = computed(() => {
		const data = this.data();
		if (!data) return {};

		return data.ventures.reduce((map, v) => {
			map[v.accVentureId] = v;
			return map;
		}, {} as Record<number, AccVentureFuncData>);
	});


	public async setSessionAttendeeStatus(accSessionId: number, ventureAndPersonIds: { accVentureId: number, personId: number }[], status: SessionAttendeeStatus) {
		await this.func.accSessionAttendee.set({ accSessionId, ventureAndPersonIds, status });
		await this.reloadData();
	}


	public async sendAttendanceEmail(accSessionId: number) {
		return this.func.accSessionAttendee.sendSessionSurveyEmail({ accSessionId });
	}



	public async setTopicSpecialists(accTopicId: number, trimesterId: number, personIds: number[]) {
		return await this.func.accTopicSpecialists.setTopicSpecialists({ accTopicId, trimesterId, personIds });
	}


	public async setVentureFinalEventId(accVentureId: number, eventId?: number) {
		await this.func.accVenture.setFinalEventId({ accVentureId, eventId });
		await this.reloadData();
	}


	public async setVentureRoadmapScore(accVentureId: number, roadmapScore: number) {
		await this.func.accVenture.setRoadmapScore({ accVentureId, roadmapScore });
		await this.reloadData();
	}

	public async setVentureFinalPresentationScore(accVentureId: number, finalPresentationScore: number) {
		await this.func.accVenture.setFinalPresentationScore({ accVentureId, finalPresentationScore });
		await this.reloadData();
	}

	public async setVentureDeckSubmittedUTC(accVentureId: number, deckSubmittedUTC: number | undefined) {
		await this.func.accVenture.setDeckSubmittedUtc({ accVentureId, deckSubmittedUTC });
		await this.reloadData();
	}

	public async setTrimesterKeynoteSpeaker(accId: number, name: string | undefined, bio: string | undefined) {
		await this.func.accelerator.setKeynoteSpeaker({ accId, name, bio });
		await this.reloadData();
	}

	public async markVenturesGraduated(accVentureIds: number[]) {
		await this.func.accVenture.markGraduated({ accVentureIds });
		await this.reloadData();
	}

	public async deleteAward(awardId: number) {
		await this.func.award.deleteAward({ awardId });
		await this.reloadData();
	}

	public async setClassSpeaker(accEntrepreneurId: number, isClassSpeaker: boolean) {
		await this.func.accEntrepreneur.setClassSpeaker({ accEntrepreneurId, isClassSpeaker });
		await this.reloadData();
	}
}